import React from 'react';
import { useNavigate } from 'react-router-dom';
import GuestLayout from '../components/GuestLayout';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Minus, Plus, Trash2, ArrowLeft, Loader2, ShoppingBag } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartPage() {
  const { items, total, updateQuantity, removeItem, loading } = useCart();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [checkingOut, setCheckingOut] = React.useState(false);

  const handleCheckout = async () => {
    if (!token) { navigate('/login'); return; }
    setCheckingOut(true);
    try {
      const res = await axios.post(`${API}/checkout`, {
        origin_url: window.location.origin
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout failed');
      setCheckingOut(false);
    }
  };

  if (items.length === 0) return (
    <GuestLayout>
      <div className="flex flex-col items-center justify-center h-[60vh] px-6">
        <div className="w-16 h-16 bg-sand rounded-full flex items-center justify-center mb-4">
          <ShoppingBag size={28} className="text-text-muted" />
        </div>
        <h2 className="font-heading text-xl text-espresso mb-2">Your cart is empty</h2>
        <p className="text-text-muted text-sm mb-6">Browse services and products to get started</p>
        <Button
          data-testid="cart-browse-btn"
          onClick={() => navigate('/services')}
          className="bg-espresso hover:bg-espresso-light text-white rounded-full px-8"
        >
          Browse Services
        </Button>
      </div>
    </GuestLayout>
  );

  return (
    <GuestLayout>
      <div className="px-6 pt-8 max-w-md mx-auto md:max-w-2xl">
        <div className="flex items-center gap-3 mb-6">
          <button data-testid="cart-back-btn" onClick={() => navigate(-1)} className="text-espresso">
            <ArrowLeft size={20} />
          </button>
          <h1 data-testid="cart-title" className="font-heading text-2xl text-espresso">Your Cart</h1>
          <span className="text-text-muted text-sm ml-auto">{items.length} item{items.length !== 1 ? 's' : ''}</span>
        </div>

        <div className="space-y-4 mb-8">
          {items.map((item) => {
            const detail = item.item_details;
            if (!detail) return null;
            return (
              <div key={item.id} data-testid={`cart-item-${item.id}`} className="bg-white border border-warm-border rounded-2xl p-4 flex gap-4">
                <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
                  <img src={detail.image_url} alt={detail.title} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-body text-sm font-semibold text-espresso">{detail.title}</h3>
                      <p className="text-text-muted text-xs capitalize">{item.item_type}</p>
                    </div>
                    <button
                      data-testid={`cart-remove-${item.id}`}
                      onClick={() => removeItem(item.id)}
                      className="text-text-muted hover:text-warm-error transition-colors p-1"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center gap-2 bg-sand rounded-full px-1 py-0.5">
                      <button
                        onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1))}
                        className="w-6 h-6 rounded-full bg-white flex items-center justify-center"
                      >
                        <Minus size={12} />
                      </button>
                      <span className="text-espresso text-sm font-semibold w-5 text-center">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="w-6 h-6 rounded-full bg-white flex items-center justify-center"
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                    <span className="text-espresso font-semibold text-sm">
                      ${(detail.price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Summary */}
        <div className="bg-white border border-warm-border rounded-2xl p-5 mb-6">
          <div className="flex justify-between mb-2">
            <span className="text-text-muted text-sm">Subtotal</span>
            <span className="text-espresso font-semibold">${total.toFixed(2)}</span>
          </div>
          <div className="border-t border-warm-border pt-3 mt-3 flex justify-between">
            <span className="text-espresso font-semibold">Total</span>
            <span data-testid="cart-total" className="text-espresso font-bold text-lg">${total.toFixed(2)}</span>
          </div>
        </div>

        <Button
          data-testid="checkout-btn"
          onClick={handleCheckout}
          disabled={checkingOut}
          className="w-full bg-espresso hover:bg-espresso-light text-white rounded-full h-14 text-base font-medium transition-transform active:scale-95 mb-4"
        >
          {checkingOut ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
          {checkingOut ? 'Redirecting to payment...' : `Pay $${total.toFixed(2)}`}
        </Button>
      </div>
    </GuestLayout>
  );
}
